/**
 * Created by Alican Köker on 2018-12-19.
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IScreenViewConfig, ScreenView} from "@nolimitcity/slot-game/bin/core/screen/ScreenView";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {EdisonGameModuleConfig} from "../../../EdisonGameModuleConfig";
import {EdisonSoundConfig} from "../../../EdisonSoundConfig";
import {EdisonReelAreaView} from "../../../reel/EdisonReelAreaView";

export class EdisonFreespinIntroView extends ScreenView {

    private _introContainer:PIXI.Container;
    private _introBGContainerSprite:PIXI.Sprite;
    private _introBGMaskContainerSprite:PIXI.Sprite;
    private _introBackground:PIXI.Sprite;
    private _introBackgroundTileLeft:PIXI.Sprite;
    private _introBackgroundTileRight:PIXI.Sprite;
    private _introBackgroundTileMask:PIXI.Graphics;
    private _orientation:Orientation;

    constructor(config:IScreenViewConfig, autoClose:boolean = true) {
        super(config, autoClose);
    }

    protected initAnimations():void {
        super.initAnimations();

        this._button.alpha = 0;

        this._introBGContainerSprite = new PIXI.Sprite();
        this._introBGMaskContainerSprite = new PIXI.Sprite();
        this._introBackground = new PIXI.Sprite(GameResources.getTextures("fsIntroBg")[0]);

        this._introBackgroundTileMask = new PIXI.Graphics();
        this._introBackgroundTileMask.beginFill(0xABCDEF, 1);
        this._introBackgroundTileMask.drawRect(-375, -265, 765, 480);
        this._introBackgroundTileMask.endFill();

        this._introBackgroundTileLeft = new PIXI.Sprite(GameResources.getTextures("reelFrameBG")[0]);
        this._introBackgroundTileRight = new PIXI.Sprite(GameResources.getTextures("reelFrameBG")[0]);

        this._introBGContainerSprite.mask = this._introBackgroundTileMask;

        this._introBGMaskContainerSprite.anchor.set(.5);
        this._introBackgroundTileLeft.anchor.set(.5);
        this._introBackgroundTileRight.anchor.set(.5);

        this._introBackground.anchor.set(.5);
        this._introBackground.scale.set(1.03, 1);


        this._introContainer = new PIXI.Container();
        this._introContainer.addChild(this._introBGContainerSprite);
        this._introContainer.addChild(this._introBGMaskContainerSprite);
        this._introBGMaskContainerSprite.addChild(this._introBackgroundTileMask);
        this._introBGContainerSprite.addChild(this._introBackground);
        this._introBGContainerSprite.addChild(this._introBackgroundTileLeft);
        this._introBGContainerSprite.addChild(this._introBackgroundTileRight);
        this.addChild(this._introContainer);

        this._introContainer.alpha = 0;
    }

    protected onResize(resizeData:IResizeData):void {
        super.onResize(resizeData);

        this._orientation = resizeData.orientation;

        const screenWidth = resizeData.width / this._layer.scale.x;
        const screenHeight = resizeData.height / this._layer.scale.y;

        if(resizeData.orientation === Orientation.LANDSCAPE) {

            this._introBackground.position.set(-5, -30);
            this._introBackgroundTileLeft.position.set(-760, -28);
            this._introBackgroundTileRight.position.set(765, -28);
            this._introBGMaskContainerSprite.position.set(screenWidth / 2 - 10, screenHeight / 2);
        } else {

            this._introBackground.position.set(0, -106);
            this._introBackgroundTileLeft.position.set(-750, -106);
            this._introBackgroundTileRight.position.set(750, -106);
            this._introBGMaskContainerSprite.position.set(screenWidth / 2, screenHeight / 2 - 85);
        }
        this._introBGContainerSprite.position.set(screenWidth / 2, screenHeight / 2);
    }

    protected startPresentation(data?:ParsedServerData, onShowComplete?:() => void):void {
        SlotGame.sound.fadeAmbience(0, 100);

        let portraitMoveUpDelay:number = this._orientation === Orientation.PORTRAIT ? 1.5 : 0;
        let startDelay:number = 1;

        let tl:TimelineLite = new TimelineLite();

        tl.add(() => {
            SlotGame.sound.playEffect(EdisonSoundConfig.instance.FS_INTRO);
        }, .2);

        if(this._orientation === Orientation.PORTRAIT) {
            tl.add(() => {
                EventHandler.dispatchEvent(new GameEvent(EdisonReelAreaView.ANIMATE_MODE_SET));
            }, startDelay);
        }

        tl.add(() => this.playFSIntroBgChangeAnimation(), portraitMoveUpDelay + startDelay);

        if(onShowComplete) {
            tl.add(() => onShowComplete(), portraitMoveUpDelay + startDelay);
        }

        tl.add(() => {
            TweenLite.to(this._introContainer, .5, {alpha : 1});
            EdisonGameModuleConfig.freespinOverlay.playInitMetersAnimations(true);
            EdisonGameModuleConfig.freespinOverlay.activateView();
        }, portraitMoveUpDelay + startDelay);

        tl.add(() => {
            TweenLite.to(this._button, .5, {alpha : 1, ease : Linear.easeNone});
            this.enableButton();
        }, portraitMoveUpDelay + startDelay + 1);
    }

    private playFSIntroBgChangeAnimation():TimelineLite {
        const animDuration:number = .3;

        const initLeft:number = -760;
        const initRight:number = 765;

        const toXLeft:number = -375;
        const toXRight:number = 375;

        this._introBackground.visible = false;

        let tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            TweenLite.to(this._introBackgroundTileLeft, animDuration, {x : toXLeft, ease : Linear.easeNone});
            TweenLite.to(this._introBackgroundTileRight, animDuration, {x : toXRight, ease : Linear.easeNone});
        });
        tl.add(() => {
            this._introBackground.visible = true;
            TweenLite.to(this._introBackgroundTileLeft, animDuration, {x : initLeft, ease : Linear.easeNone});
            TweenLite.to(this._introBackgroundTileRight, animDuration, {x : initRight, ease : Linear.easeNone});
        }, animDuration);

        return tl;
    }

    protected stopPresentation(closeCallback?:() => void) {
        super.stopPresentation(closeCallback);

        this._introContainer.alpha = 0;
        this._button.alpha = 0;
    }
}